import { uploadMenuItemImageURL } from "../index";

export const ALLOWED_UPLOAD_TYPES = [
  "image/gif",
  "image/png",
  "image/jpeg",
  "image/webp",
];

const UPLOAD_PREFIX = "custom-menu-items";

// True only for a url uploadMenuItemImage produced - never for one pasted in
// by hand, including the legacy Discord attachment links many built-in items
// still carry. Mirrors src/util/s3.ts's isUploadedMenuItemImage on the
// server, which is the one that actually decides for Discord embeds.
export const isUploadedMenuItemImage = (url?: string | null): boolean => {
  if (!url) return false;
  try {
    return new URL(url).pathname.startsWith(`/${UPLOAD_PREFIX}/`);
  } catch {
    return false;
  }
};

// Uploads to S3 via the server (see /api/upload-menu-item-image) and
// returns the resulting public url, ready to use as the item's image field -
// same as if the broadcaster had pasted a url directly.
export const uploadMenuItemImage = async (
  file: File,
  type: string,
): Promise<string> => {
  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("Not authenticated");
  }
  if (!ALLOWED_UPLOAD_TYPES.includes(file.type)) {
    throw new Error("Only gif, png, jpeg or webp images are allowed");
  }

  const body = new FormData();
  body.append("image", file);
  body.append("type", type);

  const response = await fetch(uploadMenuItemImageURL, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body,
  });
  const result = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(result?.error ?? "Upload failed");
  }
  return result.url as string;
};

/**
 * Discord CDN attachment links are signed and expiring - the `ex` param is the
 * expiry as a hex unix timestamp. Pasting one into a menu item works for a
 * while, then the image silently stops rendering in Discord once the media
 * proxy cache evicts it.
 *
 * The durable path is an upload (uploadMenuItemImage above), which lives in
 * S3 under this app's own prefix. A pasted url is only a fallback, and an
 * expiring one is a latent bug.
 */
export const getImageUrlWarning = (
  url?: string | null,
  // What to do about an expiring link.
  fixHint = "Upload the image on this item instead.",
): string | null => {
  if (!url || !url.trim()) {
    return null;
  }

  let parsed: URL;
  try {
    parsed = new URL(url.trim());
  } catch {
    return "This isn't a valid URL, so Discord will show the embed without an image.";
  }

  if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
    return "Only http and https URLs work in a Discord embed.";
  }

  const expiry = parsed.searchParams.get("ex");
  if (expiry && /^[0-9a-f]+$/i.test(expiry)) {
    const expiresAt = new Date(Number.parseInt(expiry, 16) * 1000);
    const when = expiresAt.toLocaleDateString();
    return expiresAt.getTime() < Date.now()
      ? `This Discord link expired on ${when}, so it won't render. ${fixHint}`
      : `This Discord link expires on ${when} and will stop rendering. ${fixHint}`;
  }

  return null;
};
